/* eslint-disable react/no-danger */
import _ from 'underscore';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useEffect, useCallback } from 'react';
import { Dialog, DialogType, DialogFooter, PrimaryButton, DefaultButton, Checkbox } from '@bingads-webui-react/fabric-control';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { withInstrumentation } from '@bingads-webui-react/hoc-with-instrumentation';
import { withLock, keyVault, priorityLevel } from '@bingads-webui-react/popup-manager';
import { CriticalInfo } from '@bingads-webui-campaign-react/popup-components';
import { styleClasses, getStyles } from './styles/modal-styles';
import { instrumentationSchema } from './instrumentation-schema';

const LightboxDialogContent = ({
  mainMessageBlock,
  subMessageBlock,
  accountsInfoBlock,
  midMessageBlock,
  btmMessageBlock,
  hasCheckbox,
  checkbox,
  handleRadioClick,
  selectedRadioVal,
}) => (
  <div className="lightbox-body-message panel-body">
    <div
      dangerouslySetInnerHTML={{ __html: mainMessageBlock }}
    />
    <div
      dangerouslySetInnerHTML={{ __html: subMessageBlock }}
    />
    {accountsInfoBlock &&
    <div className="paymentInfoBlock">
      <div className="addressLabel col-xs-3"><strong>{accountsInfoBlock.addressLabel}</strong></div>
      <div className="addressValue col-xs-9" dangerouslySetInnerHTML={{ __html: accountsInfoBlock.addressValue }} />
    </div>
      }
    <div
      dangerouslySetInnerHTML={{ __html: midMessageBlock }}
    />
    {accountsInfoBlock &&
    <div className="accountsInfoBlock">
      <div className="accountsLabel col-xs-3"><strong>{accountsInfoBlock.accountsLabel}</strong></div>
      <div className="accountsValue col-xs-9" dangerouslySetInnerHTML={{ __html: accountsInfoBlock.accountsInfo }} />
    </div>
      }
    <div
      dangerouslySetInnerHTML={{ __html: btmMessageBlock }}
    />
    {hasCheckbox && <Checkbox label={checkbox ? checkbox.text : ''} onChange={handleRadioClick} isChecked={selectedRadioVal} />}
  </div>
);

const LightboxDialogRaw = ({
  classes,
  hidden,
  logger,
  handleClickCancelButton,
  title,
  hasSubTitle,
  subTitle,
  content,
  hasCancelButton,
  cancelButton,
  hasActionButton,
  handleClickActionButton,
  actionButton,
  hasSecondActionButton,
  handleClickButton,
  secondActionButton,
}) => {
  useEffect(() => {
    logger.trace('lightbox dialog show');
  }, []);

  const handleClickActionButtonWithInstrumentation = useCallback(() => {
    logger.trace('lightbox dialog click action');
    handleClickActionButton();
  }, [logger, handleClickActionButton]);

  const handleClickButtonWithInstrumentation = useCallback((url, onClickActionName) => {
    logger.trace('lightbox dialog click second action');
    handleClickButton(url, onClickActionName);
  }, [logger, handleClickButton]);

  const handleClickDismissButtonWithInstrumentation = useCallback(() => {
    logger.trace('lightbox dialog dismiss');
    handleClickCancelButton();
  }, [logger, handleClickCancelButton]);

  const handleClickCancelButtonWithInstrumentation = useCallback(() => {
    logger.trace('lightbox dialog cancel');
    handleClickCancelButton();
  }, [logger, handleClickCancelButton]);

  return (
    <Dialog
      className={classNames(classes.dialog, 'lightbox')}
      hidden={hidden()}
      onDismiss={handleClickCancelButtonWithInstrumentation}
      dialogContentProps={{
        type: DialogType.normal,
        title,
        subTitle: hasSubTitle ? subTitle : '',
        showCloseButton: hasCancelButton,
      }}
      minWidth={480}
      minHeight={172}
      modalProps={{
        isBlocking: true,
        dragOptions: false,
      }}
    >
      {content}
      <DialogFooter className="panel-footer">
        {hasActionButton && <PrimaryButton className="btn btn-primary lightboxRedirect" onClick={handleClickActionButtonWithInstrumentation} text={actionButton.text} />}
        {hasSecondActionButton && <DefaultButton className="btn btn-default lightboxSecondRedirect" onClick={() => handleClickButtonWithInstrumentation(secondActionButton.url, secondActionButton.onClickActionName)} text={secondActionButton.text} />}
        {hasCancelButton && <DefaultButton className="btn btn-default lightboxRemove" onClick={handleClickDismissButtonWithInstrumentation} text={cancelButton.text} />}
      </DialogFooter>
    </Dialog>
  );
};

const LightboxDialog = withLock(LightboxDialogRaw, {
  lockKey: keyVault.lightBox,
  priority: priorityLevel.criticalInfo,
  force: true,
});

const GenericLightboxDialogContent = ({
  serverContext,
  instrumentation,
  classes,
  hidden,
  handleClickCancelButton,
  title,
  hasSubTitle,
  subTitle,
  hasCancelButton,
  cancelButton,
  mainMessageBlock,
  subMessageBlock,
  accountsInfoBlock,
  midMessageBlock,
  btmMessageBlock,
  hasCheckbox,
  checkbox,
  handleRadioClick,
  selectedRadioVal,
  hasActionButton,
  handleClickActionButton,
  actionButton,
  hasSecondActionButton,
  handleClickButton,
  secondActionButton,
}) => {
  const content = LightboxDialogContent({
    mainMessageBlock,
    subMessageBlock,
    accountsInfoBlock,
    midMessageBlock,
    btmMessageBlock,
    hasCheckbox,
    checkbox,
    handleRadioClick,
    selectedRadioVal,
  });

  if (_.result(serverContext, ['Permissions', 'IsCentralizedPopupManagementEnabled'])) {
    return (
      <CriticalInfo
        featureName={`criticalInfo_${title}`}
        title={title}
        subTitle={hasSubTitle ? subTitle : ''}
        content={content}
        primaryButtonText={actionButton.text}
        secondaryButtonText={cancelButton.text}
        onDone={handleClickActionButton}
        onDismiss={handleClickCancelButton}
        force
      />
    );
  }

  instrumentation.addScenario(instrumentationSchema);
  const logger = instrumentation.lightboxDialog.create().promptActivity.create();

  return (
    <LightboxDialog
      classes={classes}
      hidden={hidden}
      logger={logger}
      handleClickCancelButton={handleClickCancelButton}
      title={title}
      hasSubTitle={hasSubTitle}
      subTitle={subTitle}
      content={content}
      hasCancelButton={hasCancelButton}
      cancelButton={cancelButton}
      hasActionButton={hasActionButton}
      handleClickActionButton={handleClickActionButton}
      actionButton={actionButton}
      hasSecondActionButton={hasSecondActionButton}
      handleClickButton={handleClickButton}
      secondActionButton={secondActionButton}
    />
  );
};

GenericLightboxDialogContent.propTypes = {
  serverContext: PropTypes.shape({}).isRequired,
  instrumentation: PropTypes.shape({}).isRequired,
  classes: styleClasses.isRequired,
  hidden: PropTypes.func.isRequired,
  handleClickCancelButton: PropTypes.func.isRequired,
  title: PropTypes.string,
  hasSubTitle: PropTypes.bool,
  subTitle: PropTypes.string,
  hasCancelButton: PropTypes.bool,
  cancelButton: PropTypes.shape({
    url: PropTypes.string,
    onClickActionName: PropTypes.string,
    text: PropTypes.string,
  }),
  mainMessageBlock: PropTypes.string,
  subMessageBlock: PropTypes.string,
  midMessageBlock: PropTypes.string,
  btmMessageBlock: PropTypes.string,
  accountsInfoBlock: PropTypes.shape({
    addressLabel: PropTypes.string,
    addressValue: PropTypes.string,
    accountsLabel: PropTypes.string,
    accountsInfo: PropTypes.string,
  }),
  hasCheckbox: PropTypes.bool,
  checkbox: PropTypes.shape({
    text: PropTypes.string,
  }),
  handleRadioClick: PropTypes.func.isRequired,
  selectedRadioVal: PropTypes.bool.isRequired,
  hasActionButton: PropTypes.bool,
  handleClickActionButton: PropTypes.func.isRequired,
  actionButton: PropTypes.shape({
    url: PropTypes.string,
    onClickActionName: PropTypes.string,
    text: PropTypes.string,
  }),
  hasSecondActionButton: PropTypes.bool,
  handleClickButton: PropTypes.func.isRequired,
  secondActionButton: PropTypes.shape({
    url: PropTypes.string,
    onClickActionName: PropTypes.string,
    text: PropTypes.string,
  }),
};

GenericLightboxDialogContent.defaultProps = {
  hasSubTitle: false,
  hasActionButton: false,
  hasCancelButton: false,
  actionButton: {},
  cancelButton: {},
  checkbox: null,
  accountsInfoBlock: null,
  hasCheckbox: false,
  hasSecondActionButton: false,
  secondActionButton: {},
  title: '',
  subTitle: '',
  mainMessageBlock: '',
  subMessageBlock: '',
  midMessageBlock: '',
  btmMessageBlock: '',
};

export const GenericLightboxDialog = _.compose(
  withInstrumentation,
  WrappedComponent => withDefaultStyles(WrappedComponent, getStyles)
)(GenericLightboxDialogContent);